import React from 'react';

/**
 * Mini screen preview component for dashboard
 * Renders actual screen content at a small scale
 */
const ScreenPreview = ({ screen, globalSettings }) => {
    const containerRef = React.useRef(null);
    const [scale, setScale] = React.useState(0.25); // Default start scale

    const baseWidth = screen?.width || 375;
    const baseHeight = screen?.height || 812;

    React.useEffect(() => {
        if (!containerRef.current) return;

        const updateScale = () => {
            const { height } = containerRef.current.getBoundingClientRect();
            if (height > 0) {
                setScale(height / baseHeight);
            }
        };

        const observer = new ResizeObserver(updateScale);
        observer.observe(containerRef.current);

        // Initial calculation
        updateScale();

        return () => observer.disconnect();
    }, [baseHeight]);

    if (!screen) return null;

    return (
        <div
            ref={containerRef}
            className="h-full aspect-[9/19.5] rounded-md border border-border/50 shadow-sm relative overflow-hidden bg-white"
            style={{
                backgroundColor: screen.backgroundColor || '#ffffff',
            }}
        >
            <div
                style={{
                    width: `${baseWidth}px`,
                    height: `${baseHeight}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    position: 'absolute',
                    fontFamily: globalSettings?.fontFamily || 'Inter, sans-serif',
                }}
            >
                {(screen.elements || []).map((element) => {
                    const commonStyle = {
                        position: 'absolute',
                        left: `${element.x}px`,
                        top: `${element.y}px`,
                        width: `${element.width}px`,
                        height: `${element.height}px`,
                        ...element.styles,
                    };

                    if (element.type === 'text') {
                        return (
                            <div key={element.id} style={commonStyle}>
                                {element.content || 'Text'}
                            </div>
                        );
                    }

                    if (element.type === 'button') {
                        return (
                            <button key={element.id} style={commonStyle}>
                                {element.content || 'Button'}
                            </button>
                        );
                    }

                    if (element.type === 'image') {
                        return (
                            <div
                                key={element.id}
                                style={{
                                    ...commonStyle,
                                    backgroundColor: '#f3f4f6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {element.imageUrl ? (
                                    <img
                                        src={element.imageUrl}
                                        alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <span style={{ fontSize: '10px', color: '#9ca3af' }}>IMG</span>
                                )}
                            </div>
                        );
                    }

                    if (element.type === 'progress-bar') {
                        return (
                            <div
                                key={element.id}
                                style={{
                                    ...commonStyle,
                                    backgroundColor: element.styles?.backgroundColor || '#f3f4f6',
                                    borderRadius: element.styles?.borderRadius || '4px',
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        width: `${element.progress || 0}%`,
                                        height: '100%',
                                        backgroundColor: element.styles?.fillColor || '#3b82f6',
                                    }}
                                />
                            </div>
                        );
                    }

                    if (element.type === 'input') {
                        return (
                            <div
                                key={element.id}
                                style={{
                                    ...commonStyle,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                }}
                            >
                                {element.label && (
                                    <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                                        {element.label}
                                    </label>
                                )}
                                <input
                                    type="text"
                                    placeholder={element.placeholder}
                                    disabled
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        padding: '8px',
                                        borderRadius: element.styles?.borderRadius || '4px',
                                        border: `${element.styles?.borderWidth || '1px'} solid ${element.styles?.borderColor || '#e5e7eb'}`,
                                        backgroundColor: element.styles?.backgroundColor || '#ffffff',
                                        fontSize: element.styles?.fontSize || '14px',
                                        color: element.styles?.color || '#1f2937',
                                    }}
                                />
                            </div>
                        );
                    }

                    // Fallback for other types
                    return (
                        <div
                            key={element.id}
                            style={{
                                ...commonStyle,
                                backgroundColor: '#e5e7eb',
                                borderRadius: '2px',
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default ScreenPreview;
